
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, Brain, Loader2, TrendingUp, Heart, AlertTriangle, Lightbulb, ArrowRight, Target, Download, FileText } from 'lucide-react';
import { AnalysisResults } from '../types/notes';
import { exportToJTBD } from '@/services/export';
import UsageIndicator from './UsageIndicator';
import { useUsageTracking } from '@/hooks/useUsageTracking';

interface AnalysisPanelProps {
  isOpen: boolean;
  onClose: () => void;
  isAnalyzing: boolean;
  results: AnalysisResults | null;
  isMultiInterview?: boolean;
  interviewNames?: string[];
}

export const AnalysisPanel = ({ isOpen, onClose, isAnalyzing, results, isMultiInterview = false, interviewNames = [] }: AnalysisPanelProps) => {
  const { currentUsage, limit } = useUsageTracking();
  
  if (!isOpen) return null;

  return (
    <div className="w-96 bg-white border-l border-gray-200 shadow-xl flex flex-col">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Brain className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-900">
              {isMultiInterview ? 'Cross-Interview Analysis' : 'AI Analysis'}
            </h2>
          </div>
          <div className="flex items-center space-x-2">
            {results && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => exportToJTBD(results, isMultiInterview, interviewNames)}
                className="flex items-center space-x-1"
              >
                <Download className="w-3 h-3" />
                <span className="hidden sm:inline">Export JTBD</span>
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          {isMultiInterview 
            ? `Patterns and insights across ${interviewNames.length} interviews`
            : 'Insights and patterns from your interview notes'
          }
        </p>
        
        {/* Usage Indicator */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <UsageIndicator currentUsage={currentUsage} limit={limit} />
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-6 space-y-6">
          {isAnalyzing ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="relative">
                <Brain className="w-12 h-12 text-primary animate-pulse-soft" />
                <Loader2 className="w-6 h-6 text-primary animate-spin absolute -top-1 -right-1" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="font-medium text-gray-900">Analyzing Interview Data</h3>
                <p className="text-sm text-gray-600">
                  AI is processing your notes to identify patterns and insights...
                </p>
              </div>
              <div className="w-full max-w-xs bg-gray-200 rounded-full h-2">
                <div className="bg-primary h-2 rounded-full animate-pulse w-2/3"></div>
              </div>
            </div>
          ) : results ? (
            <div className="space-y-6">
              {/* TL;DR Recommendations at the top */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <ArrowRight className="w-4 h-4 text-secondary" />
                  <h3 className="font-medium text-gray-900">TL;DR</h3>
                  <Badge variant="outline" className="text-xs">
                    {results.recommendations.length}
                  </Badge>
                </div>
                <Card className="p-4 bg-secondary/5 border-secondary/20">
                  <ul className="space-y-2">
                    {results.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm">
                        <div className="w-1.5 h-1.5 bg-secondary rounded-full mt-2 flex-shrink-0" />
                        <span className="text-gray-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </div>
              
              <Separator />
              
              <AnalysisSection
                title="Key Themes"
                icon={TrendingUp}
                items={results.themes}
                color="text-primary"
              />
              
              <AnalysisSection
                title="Emotional Patterns"
                icon={Heart}
                items={results.emotions}
                color="text-pink-600"
              />
              
              <AnalysisSection
                title="Pain Points"
                icon={AlertTriangle}
                items={results.painPoints}
                color="text-red-600"
              />
              
              <AnalysisSection
                title="Key Insights"
                icon={Lightbulb}
                items={results.insights}
                color="text-green-600"
              />
              
              <AnalysisSection
                title="Opportunities"
                icon={Target}
                items={results.opportunities || []}
                color="text-purple-600"
              />
            </div>
          ) : (
            <div className="text-center py-12 space-y-4">
              <Brain className="w-12 h-12 text-gray-400 mx-auto" />
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900">No Analysis Yet</h3>
                <p className="text-sm text-gray-600">
                  Add some notes to your canvas and click "Analyze Interview" to get AI-powered insights.
                </p>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

interface AnalysisSectionProps {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  items: string[];
  color: string;
}

const AnalysisSection = ({ title, icon: Icon, items, color }: AnalysisSectionProps) => (
  <div className="space-y-3">
    <div className="flex items-center space-x-2">
      <Icon className={`w-4 h-4 ${color}`} />
      <h3 className="font-medium text-gray-900">{title}</h3>
      <Badge variant="outline" className="text-xs">
        {items.length}
      </Badge>
    </div>
    <div className="space-y-2">
      {items.map((item, index) => (
        <Card key={index} className="p-3 border-l-4 border-l-gray-200 hover:border-l-primary transition-colors">
          <p className="text-sm text-gray-700">{item}</p>
        </Card>
      ))}
    </div>
  </div>
);
